import { inject, Injectable, Signal, TemplateRef } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { FormGridGroupConfig } from '@modules/forms/components/form-grid/models/form-grid-group-config';
import { FormGridGroupMode } from '@modules/forms/components/form-grid/models/form-grid-group-mode';
import { FormGridActionsService } from '@modules/forms/components/form-grid/services/form-grid-actions.service';
import { ModuleResolverInputs } from '@modules/forms/models/module-resolver-inputs';
import { SadmEntityFormType } from '@modules/sadm/models/sadm-entity-form-type';
import { SadmClient } from '@pixacare/dtos';
import { FormEngineEvents } from '@pixacare/forms';
import { SadmGridEntity } from '../models/sadm-grid-entity';

type SadmFormGroupsBuilderOptions = {
  analysisProjection: TemplateRef<{ $implicit: SadmGridEntity; mode: FormGridGroupMode }>;
  moduleInputs: (entity: SadmGridEntity) => Signal<ModuleResolverInputs>;
  onAnalysisModuleTriggered: (entity: SadmGridEntity, event: FormEngineEvents['module-applied']) => void;
  onAnalysisModuleReset: (entity: SadmGridEntity, event: FormEngineEvents['module-reset']) => void;
};

@Injectable({ providedIn: 'root' })
export class SadmFormGroupsBuilderService {
  private readonly translocoService = inject(TranslocoService);
  private readonly actions = inject(FormGridActionsService);

  build({
    analysisProjection,
    moduleInputs,
    onAnalysisModuleTriggered,
    onAnalysisModuleReset,
  }: SadmFormGroupsBuilderOptions): FormGridGroupConfig<SadmEntityFormType, SadmGridEntity, SadmClient>[] {
    return [
      {
        type: 'creation',
        label: this.translocoService.translate('Caractéristiques de la lésion'),
        form: (sadmClient) => sadmClient.entityCreationForm,
        answer: (entity) => entity.creationAnswer ?? null,
        date: (entity) => entity.createdOn ?? null,
        actions: (ctx) => [this.actions.edit(ctx)],
      },
      {
        type: 'protocol',
        label: this.translocoService.translate('Protocole de soin'),
        form: (sadmClient) => sadmClient.protocolCreationForm ?? null,
        answer: (entity) => entity.latestProtocol?.answer ?? null,
        date: (entity) => entity.latestProtocol?.createdOn ?? null,
        actions: (ctx) => [this.actions.edit(ctx), this.actions.clear(ctx)],
        editCreatesNewEntry: true,
      },
      {
        type: 'analysis',
        label: this.translocoService.translate('Analyse de la lésion'),
        form: (sadmClient) => sadmClient.analysisCreationForm,
        answer: (entity) => entity.analysis?.formAnswer ?? null,
        date: (entity) => entity.analysis?.createdOn ?? null,
        actions: (ctx) => [...this.actions.defaults(ctx), this.actions.clear(ctx)],
        moduleInputs: (entity) => moduleInputs(entity),
        projection: { label: this.translocoService.translate('Photos'), required: true, content: analysisProjection },
        onModuleTriggered: (entity, event) => onAnalysisModuleTriggered(entity, event),
        onModuleReset: (entity, event) => onAnalysisModuleReset(entity, event),
      },
    ];
  }
}
